import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { ArrowSquareOut, LinkSimple, LinkSimpleBreak } from 'phosphor-react';
import { useActionToast } from '../../../services/actionToast';
import { PageTitle } from '../../../components/PageTitle';
import { DataSkeleton, EmptyState } from '../../../components/states';
import { api } from '../../../services/api';
import { useAuth } from '../../../providers/auth';
import { SelectFilter } from '../../../components/inputs/filters/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  ConfirmDialog,
  FramedCard,
  FramedCardContent,
  FramedCardHeader,
  SearchField,
  Toolbar,
} from '../../../components/ui';

interface Institution {
  id: number;
  name: string;
  template_strategy?: 'DEFAULT' | 'MANUAL' | null;
}

interface Template {
  id: number;
  name: string;
  description?: string;
  type?: string;
  user_type?: string;
  school_class?: { id: number; name: string };
  category?: { id: number; name: string };
  is_official: boolean;
  link?: string | null;
  file?: { original?: string | null };
}

interface LinkedTemplate extends Template {
  link_id: number;
  custom_name?: string;
  custom_description?: string;
  is_active: boolean;
}

const TemplateMeta = ({ template }: { template: Template }) => (
  <Flex gap="4px" flexWrap="wrap">
    {template.school_class && <Badge variant="info">{template.school_class.name}</Badge>}
    {template.category && <Badge variant="neutral">{template.category.name}</Badge>}
    {template.user_type && <Badge variant="neutral">{template.user_type}</Badge>}
    {template.type && <Badge variant="neutral">{template.type}</Badge>}
  </Flex>
);

export function InstitutionTemplateLinkPage() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<number | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [linkedTemplates, setLinkedTemplates] = useState<LinkedTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<Set<number>>(new Set());
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedTemplate | null>(null);
  const institutionRequestVersion = useRef(0);
  const toast = useActionToast();
  const { user } = useAuth();

  const selectedInstitutionData = useMemo(
    () => institutions.find((inst) => inst.id === selectedInstitution) ?? null,
    [institutions, selectedInstitution]
  );
  const isDefaultStrategy = selectedInstitutionData?.template_strategy === 'DEFAULT';

  const filteredAvailable = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return availableTemplates;
    return availableTemplates.filter((t) =>
      [t.name, t.description, t.type, t.user_type, t.category?.name, t.school_class?.name].some(
        (v) => (v ?? '').toLowerCase().includes(term)
      )
    );
  }, [availableTemplates, searchTerm]);

  useEffect(() => {
    loadInstitutions();
  }, []);

  useEffect(() => {
    institutionRequestVersion.current += 1;
    const version = institutionRequestVersion.current;
    setAvailableTemplates([]);
    setLinkedTemplates([]);
    setSelectedTemplates(new Set());
    setSearchTerm('');
    if (selectedInstitution) {
      loadAvailableTemplates(selectedInstitution, version);
      loadLinkedTemplates(selectedInstitution, version);
    } else {
      setLoadingAvailable(false);
      setLoadingLinked(false);
    }
  }, [selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      setLoadingInstitutions(true);
      const response = await api.get('/v1/admin/institutions');
      setInstitutions(response.data.data || []);
    } catch {
      toast({ title: 'Erro ao carregar instituições', status: 'error', duration: 3000 });
    } finally {
      setLoadingInstitutions(false);
    }
  };

  const loadAvailableTemplates = async (
    institutionId = selectedInstitution,
    version = institutionRequestVersion.current
  ) => {
    if (!institutionId) return;
    try {
      setLoadingAvailable(true);
      const response = await api.get(`/v1/admin/institutions/${institutionId}/templates/available`);
      if (institutionRequestVersion.current === version) {
        setAvailableTemplates(response.data.data || []);
      }
    } catch {
      if (institutionRequestVersion.current === version) {
        setAvailableTemplates([]);
        toast({ title: 'Erro ao carregar templates disponíveis', status: 'error', duration: 3000 });
      }
    } finally {
      if (institutionRequestVersion.current === version) setLoadingAvailable(false);
    }
  };

  const loadLinkedTemplates = async (
    institutionId = selectedInstitution,
    version = institutionRequestVersion.current
  ) => {
    if (!institutionId) return;
    try {
      setLoadingLinked(true);
      const response = await api.get(`/v1/admin/institutions/${institutionId}/templates`);
      if (institutionRequestVersion.current === version) {
        setLinkedTemplates(response.data.data || []);
      }
    } catch {
      if (institutionRequestVersion.current === version) {
        setLinkedTemplates([]);
        toast({ title: 'Erro ao carregar templates vinculados', status: 'error', duration: 3000 });
      }
    } finally {
      if (institutionRequestVersion.current === version) setLoadingLinked(false);
    }
  };

  const toggleTemplate = (templateId: number) => {
    const next = new Set(selectedTemplates);
    if (next.has(templateId)) next.delete(templateId);
    else next.add(templateId);
    setSelectedTemplates(next);
  };

  const toggleAllVisible = (checked: boolean) => {
    const next = new Set(selectedTemplates);
    filteredAvailable.forEach((t) => (checked ? next.add(t.id) : next.delete(t.id)));
    setSelectedTemplates(next);
  };

  const handleLinkTemplates = async () => {
    if (!selectedInstitution || selectedTemplates.size === 0) return;
    try {
      setLoadingAction(true);
      await Promise.all(
        Array.from(selectedTemplates).map((templateId) =>
          api.post(`/v1/admin/institutions/${selectedInstitution}/templates/${templateId}`)
        )
      );
      toast({
        title:
          selectedTemplates.size === 1
            ? 'Template vinculado com sucesso'
            : `${selectedTemplates.size} templates vinculados com sucesso`,
        status: 'success',
        duration: 3000,
      });
      setSelectedTemplates(new Set());
      await Promise.all([loadAvailableTemplates(), loadLinkedTemplates()]);
    } catch {
      toast({ title: 'Erro ao vincular templates', status: 'error', duration: 3000 });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUnlink = async () => {
    if (!selectedInstitution || !unlinkTarget) return;
    try {
      setLoadingAction(true);
      await api.delete(
        `/v1/admin/institutions/${selectedInstitution}/templates/${unlinkTarget.id}`
      );
      toast({ title: 'Template desvinculado com sucesso', status: 'success', duration: 3000 });
      setUnlinkTarget(null);
      await Promise.all([loadAvailableTemplates(), loadLinkedTemplates()]);
    } catch {
      toast({ title: 'Erro ao desvincular template', status: 'error', duration: 3000 });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleActive = async (template: LinkedTemplate) => {
    try {
      setLoadingAction(true);
      await api.put(`/v1/admin/institutions/links/${template.link_id}`, {
        is_active: !template.is_active,
      });
      toast({
        title: template.is_active ? 'Template desativado' : 'Template ativado',
        status: 'success',
        duration: 2000,
      });
      await loadLinkedTemplates();
    } catch {
      toast({ title: 'Erro ao atualizar status', status: 'error', duration: 3000 });
    } finally {
      setLoadingAction(false);
    }
  };

  const canManageLinks = user?.group?.id === 1 || user?.group?.id === 3;

  if (!canManageLinks) {
    return (
      <Box>
        <PageTitle description="Defina quais modelos de conteúdo cada instituição usa.">
          Vincular Templates a Instituições
        </PageTitle>
        <Alert variant="warning">
          <AlertDescription>Você não tem permissão para acessar esta página.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  const allVisibleSelected =
    filteredAvailable.length > 0 && filteredAvailable.every((t) => selectedTemplates.has(t.id));
  const someVisibleSelected =
    !allVisibleSelected && filteredAvailable.some((t) => selectedTemplates.has(t.id));
  const institutionOptions = institutions.map((inst) => ({ label: inst.name, value: inst.id }));

  const renderAvailable = () => {
    if (loadingAvailable) {
      return <DataSkeleton variant="table" rows={4} columns={3} label="Carregando disponíveis" />;
    }
    if (filteredAvailable.length === 0) {
      return (
        <EmptyState
          variant="bare"
          title={searchTerm ? 'Nenhum template para esta busca' : 'Nenhum template disponível'}
          description={
            searchTerm
              ? 'Limpe a busca para consultar todos os templates disponíveis.'
              : 'Todos os templates já estão vinculados a esta instituição.'
          }
          action={
            searchTerm ? { label: 'Limpar busca', onClick: () => setSearchTerm('') } : undefined
          }
        />
      );
    }
    return (
      <Table variant="serra">
        <Thead>
          <Tr>
            <Th w="44px" pr={0}>
              <Checkbox
                aria-label="Selecionar todos"
                isChecked={allVisibleSelected}
                isIndeterminate={someVisibleSelected}
                onChange={(e) => toggleAllVisible(e.target.checked)}
                isDisabled={loadingAction}
              />
            </Th>
            <Th>Template</Th>
            <Th>Classificação</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filteredAvailable.map((template) => {
            const checked = selectedTemplates.has(template.id);
            return (
              <Tr
                key={template.id}
                cursor="pointer"
                bg={checked ? 'surface.selected' : undefined}
                onClick={() => !loadingAction && toggleTemplate(template.id)}
              >
                <Td pr={0} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    aria-label={`Selecionar ${template.name}`}
                    isChecked={checked}
                    onChange={() => toggleTemplate(template.id)}
                    isDisabled={loadingAction}
                  />
                </Td>
                <Td>
                  <Flex align="center" gap="8px">
                    <Text fontWeight={500}>{template.name}</Text>
                    {template.is_official && <Badge variant="success">Oficial</Badge>}
                  </Flex>
                  {template.description && (
                    <Text fontSize="12px" lineHeight="16px" color="content.subtle" noOfLines={1}>
                      {template.description}
                    </Text>
                  )}
                </Td>
                <Td>
                  <TemplateMeta template={template} />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    );
  };

  const renderLinked = () => {
    if (loadingLinked) {
      return <DataSkeleton variant="table" rows={4} columns={3} label="Carregando vinculados" />;
    }
    if (linkedTemplates.length === 0) {
      return (
        <EmptyState
          variant="bare"
          title="Nenhum template vinculado"
          description="Selecione templates à esquerda e clique em Vincular para disponibilizar materiais nesta instituição."
        />
      );
    }
    return (
      <Table variant="serra">
        <Thead>
          <Tr>
            <Th>Template</Th>
            <Th>Classificação</Th>
            <Th w="90px">Ativo</Th>
            <Th w="88px" />
          </Tr>
        </Thead>
        <Tbody>
          {linkedTemplates.map((template) => {
            const resourceLink = template.link || template.file?.original || null;
            const disableRemoval = template.is_official || linkedTemplates.length <= 1;
            let removalHint = 'Desvincular';
            if (template.is_official) removalHint = 'O template padrão não pode ser removido';
            else if (linkedTemplates.length <= 1) removalHint = 'Mantenha ao menos um template';
            return (
              <Tr key={template.link_id}>
                <Td>
                  <Flex align="center" gap="8px">
                    <Text fontWeight={500}>{template.custom_name || template.name}</Text>
                    <Badge variant={template.is_official ? 'success' : 'info'}>
                      {template.is_official ? 'Padrão' : 'Personalizado'}
                    </Badge>
                  </Flex>
                  {(template.custom_description || template.description) && (
                    <Text fontSize="12px" lineHeight="16px" color="content.subtle" noOfLines={1}>
                      {template.custom_description || template.description}
                    </Text>
                  )}
                </Td>
                <Td>
                  <TemplateMeta template={template} />
                </Td>
                <Td>
                  <Switch
                    aria-label={template.is_active ? 'Desativar' : 'Ativar'}
                    isChecked={template.is_active}
                    onChange={() => handleToggleActive(template)}
                    isDisabled={loadingAction}
                  />
                </Td>
                <Td pr="16px">
                  <Flex justify="flex-end" gap="2px">
                    {resourceLink && (
                      <IconButton
                        aria-label="Abrir material"
                        icon={<ArrowSquareOut size={16} />}
                        size="sm"
                        variant="ghost"
                        color="content.body"
                        onClick={() => window.open(resourceLink, '_blank', 'noopener,noreferrer')}
                      />
                    )}
                    <Tooltip label={removalHint}>
                      <IconButton
                        aria-label="Desvincular"
                        icon={<LinkSimpleBreak size={16} />}
                        size="sm"
                        variant="ghost"
                        color="content.body"
                        _hover={{ bg: 'state.destructiveSurface', color: 'state.destructive' }}
                        isDisabled={disableRemoval || loadingAction}
                        onClick={() => setUnlinkTarget(template)}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    );
  };

  return (
    <Box>
      <PageTitle description="Defina quais modelos de conteúdo cada instituição usa.">
        Vincular Templates a Instituições
      </PageTitle>

      <Flex align="center" gap="12px" mb="16px" flexWrap="wrap">
        <Box w={{ base: '100%', md: '360px' }}>
          <SelectFilter
            name="institution"
            inputId="institution-select"
            placeholder="Selecione uma instituição"
            options={institutionOptions}
            value={selectedInstitution}
            onChange={(value: number | undefined) => setSelectedInstitution(value ?? null)}
            isSearchable
            isDisabled={loadingInstitutions}
            isLoading={loadingInstitutions}
          />
        </Box>
        {selectedInstitutionData && (
          <Text fontSize="13px" color="content.body">
            {linkedTemplates.length}{' '}
            {linkedTemplates.length === 1 ? 'template vinculado' : 'templates vinculados'}
          </Text>
        )}
      </Flex>

      {!selectedInstitution && (
        <Alert variant="info">
          <AlertTitle>Como funcionam os templates?</AlertTitle>
          <AlertDescription>
            Um template agrupa módulos, categorias, materiais extras e tópicos. Ao vinculá-lo a uma
            instituição, toda essa estrutura fica disponível sem precisar recriar cada item.
            Selecione uma instituição acima para começar.
          </AlertDescription>
        </Alert>
      )}

      {selectedInstitution && (
        <Flex direction="column" gap="16px">
          {isDefaultStrategy && (
            <Alert variant="info">
              <AlertDescription>
                Esta instituição foi criada com o template padrão. Revise os templates oficiais e
                vincule apenas os conteúdos que deseja disponibilizar.
              </AlertDescription>
            </Alert>
          )}

          <Flex
            direction={{ base: 'column', xl: 'row' }}
            align="stretch"
            gap="16px"
            data-testid="template-link-panels"
          >
            <FramedCard flex="1" minW={0}>
              <FramedCardHeader px="16px" py="8px">
                <Flex align="center" justify="space-between" gap="12px">
                  <Text fontSize="13px" fontWeight={500} color="content.strong">
                    Templates disponíveis
                  </Text>
                  <Badge variant="neutral">{availableTemplates.length}</Badge>
                </Flex>
              </FramedCardHeader>
              <FramedCardContent p={0} gap={0}>
                <Toolbar justify="space-between">
                  <SearchField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    label="Buscar templates disponíveis"
                    placeholder="Buscar por nome, categoria ou público"
                    width="240px"
                  />
                  <Button
                    size="sm"
                    leftIcon={<LinkSimple size={14} />}
                    onClick={handleLinkTemplates}
                    isLoading={loadingAction}
                    isDisabled={selectedTemplates.size === 0}
                  >
                    {selectedTemplates.size > 0 ? `Vincular ${selectedTemplates.size}` : 'Vincular'}
                  </Button>
                </Toolbar>
                {renderAvailable()}
              </FramedCardContent>
            </FramedCard>

            <FramedCard flex="1" minW={0}>
              <FramedCardHeader px="16px" py="8px">
                <Flex align="center" justify="space-between" gap="12px">
                  <Text fontSize="13px" fontWeight={500} color="content.strong">
                    Templates vinculados
                  </Text>
                  <Badge variant="neutral">{linkedTemplates.length}</Badge>
                </Flex>
              </FramedCardHeader>
              <FramedCardContent p={0} gap={0}>
                {renderLinked()}
              </FramedCardContent>
            </FramedCard>
          </Flex>
        </Flex>
      )}

      <ConfirmDialog
        isOpen={Boolean(unlinkTarget)}
        onClose={() => setUnlinkTarget(null)}
        onConfirm={handleUnlink}
        title="Desvincular template"
        description={`O template "${
          unlinkTarget?.custom_name || unlinkTarget?.name
        }" deixará de estar disponível nesta instituição.`}
        confirmLabel="Desvincular"
        isDestructive
        isLoading={loadingAction}
      />
    </Box>
  );
}
